import { useEffect, useRef } from 'react'
import { onStreamOpen } from '../servers/commandStream'

/**
 * The bounded ladder, long enough to outlast a machine's own restart and still
 * end rather than become a poll (owner, 2026-08-24 — an 8s ladder gave up while
 * an edge was still coming back).
 */
const WAITS = [800, 2000, 5000, 10_000, 20_000, 30_000, 30_000]

/**
 * Re-ask a fan-out query while some machine has not answered — bounded, and
 * immediately when the account stream (re)opens.
 *
 * The rail carried this inline. The STAGE did not, and on a phone the stage is a
 * full-screen push that UNMOUNTS the rail — so a selected agent whose machine was
 * slow sat on "Restoring your session… Waiting for <machine> to answer" with
 * nothing retrying, until a manual reload (owner, 2026-08-25; a reload fixed it
 * instantly because it rebuilds the socket and re-runs the fan-out). The two
 * views also read DIFFERENT agent queries (`['agents']` vs `['agents','all']`),
 * so the rail's ladder could never have covered the stage even when mounted.
 *
 * App is always mounted, so this hook lives there too, driving the stage's own
 * query. onStreamOpen is the load-bearing half on mobile: the liveness probe
 * closes a half-open socket on foreground, the reconnect fires this, and the
 * fan-out that failed against the dead socket is re-sent on the live one.
 */
export function useAnswerRetry(
  failedCount: number,
  dataUpdatedAt: number,
  refetch: () => void,
): void {
  const tries = useRef(0)
  useEffect(() => {
    if (!failedCount) {
      tries.current = 0
      return
    }
    const wait = WAITS[tries.current]
    if (wait === undefined) return // ladder exhausted — bounded, never a poll
    const t = window.setTimeout(() => {
      tries.current += 1
      refetch()
    }, wait)
    return () => window.clearTimeout(t)
    // dataUpdatedAt so each answer schedules the next attempt (or stops).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedCount, dataUpdatedAt])

  useEffect(
    () =>
      onStreamOpen(() => {
        // The pipe is back: ask now rather than waiting out a backoff, and reset
        // the ladder so a machine that comes and goes gets the full patience each
        // time.
        tries.current = 0
        refetch()
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
}
